import { ActionType } from '../../../stores/action-types';
import Node from '@material-ui/icons/Memory';
import Refresh from '@material-ui/icons/Refresh';
import CustomButton from '../../../../components/CustomButtons/ButtonTS';
import Button from '@material-ui/core/Button';
import Card from '../../../../components/Card/CardTS';
import CardBody from '../../../../components/Card/CardBody';
import CardHeader from '../../../../components/Card/CardHeaderTS';
import CardIcon from '../../../../components/Card/CardIcon.js';
import React, { useCallback, ChangeEvent } from 'react';
import regularFormsStyle from '../../../../assets/jss/material-dashboard-pro-react/views/regularFormsStyleTS';
import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import { addNode, getFreeDevices } from '../../../stores/actions/installerActions';
import { connect } from 'react-redux';
import Loading from '../../../components/Loading/Loading';
import Snackbar from '../../../../components/Snackbar/Snackbar';
import ErrorComponent from '../../../components/ErrorComponent/ErrorComponent';
import ErrorText from '../../../components/ErrorComponent/ErrorText';
import '../InstallerPanel.css';
import { headerStyleSuccess, headerStyleFail, emptyHeaderStyle } from '../../../helpers/GlobalVariables';
import utils from '../../../helpers/GlobalVariables';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { ISystem } from '../../../helpers/interfaces';
import { IFreeDevice, ICreateNodeForm } from './definitions';
import GridItem from '../../../../components/Grid/GridItem';
import { SYSTEM_VERSIONS } from '../../../helpers/Systems';
import _ from 'lodash';

const CreateNodeForm = ({
	classes,
	addNode,
	disableCreateNodeButton,
	disableRefreshButton,
	FreeDevices,
	getFreeDevices,
	systems,
	createNodeFail,
	createNodeSuccess,
	mobileView,
}: ICreateNodeForm) => {
	const [deviceName, setDeviceName] = useState<string>(null);
	const [inputValue, setInputValue] = useState<string>('');
	const [error, setError] = useState<boolean>(false);
	const dispatch = useDispatch();
	const closeNotificationTimer = useRef(null);
	const [pickedSystemName, setPickedSystem] = useState(null);
	const devices = FreeDevices.filter((device) => device.SystemVersion === SYSTEM_VERSIONS.W4.valueOf());
	const [filteredDevices, setFilteredDevices] = useState<IFreeDevice[]>(devices);

	const closeCreateNodeNotification = useCallback(() => {
		dispatch({ type: ActionType.CLOSE_CREATE_NODE_NOTIFICATION });
	}, [dispatch]);

	useEffect(() => {
		getFreeDevices();
		closeCreateNodeNotification();
	}, [getFreeDevices, closeCreateNodeNotification]);

	useEffect(() => {
		if (createNodeFail || createNodeSuccess) {
			clearTimeout(closeNotificationTimer.current);
			closeNotificationTimer.current = setTimeout(closeCreateNodeNotification, 6000);
		}
		setFilteredDevices(FreeDevices.filter((device) => device.SystemVersion === SYSTEM_VERSIONS.W4.valueOf()));
		return () => {
			clearTimeout(closeNotificationTimer.current);
		};
	}, [FreeDevices, closeCreateNodeNotification, createNodeFail, createNodeSuccess, setFilteredDevices]);

	const handleInputChange = (e: ChangeEvent): void => {
		const currentValue = (e.target as HTMLInputElement).value;
		const containsIncorrectSigns = utils.validateInputChars(currentValue);

		setError(containsIncorrectSigns);
		setInputValue(currentValue);
	};

	const handleAddDevice = (): void => {
		closeCreateNodeNotification();
		const nodeName = inputValue;
		const [currentPickedSystem] = systems.length > 0 ? systems.filter((s) => s.name === pickedSystemName) : null;
		const [currentPickedDevice] = devices.filter((d) => d.SerialString === deviceName);

		addNode(currentPickedSystem.ID, currentPickedDevice.SerialString, nodeName);
	};

	const onCreateNodeButtonClick = (): JSX.Element => {
		if (disableCreateNodeButton) {
			const style = {
				marginBottom: '10px',
				marginTop: '25px',
				paddingLeft: '10px',
				paddingTop: '5px',
			};
			return (
				<div style={style}>
					<Loading transform={1} height={17} width={40} color='darkGray' />
				</div>
			);
		}
		return (
			<Button
				style={{ marginTop: error ? '5px' : '25px', transition: 'color 0.3s' }}
				disabled={error || !inputValue.length || !deviceName || createNodeFail || createNodeSuccess}
				onClick={handleAddDevice}
				data-testid='createNodeButton'
			>
				Create
			</Button>
		);
	};

	const onRefreshButtonClick = (): JSX.Element => {
		if (disableRefreshButton) {
			return (
				<CustomButton
					disabled={disableRefreshButton}
					simple
					justIcon
					color='info'
					style={{ width: '40px', paddingLeft: '10px', paddingRight: '10px' }}
				>
					<Loading transform={1} height={17} width={20} color='blue' />
				</CustomButton>
			);
		}

		return (
			<CustomButton
				simple
				justIcon
				color='info'
				onClick={() => getFreeDevices()}
				disabled={disableRefreshButton}
			>
				<Refresh />
			</CustomButton>
		);
	};

	const renderMobileNotifications = (): JSX.Element => {
		const headerStyle = createNodeFail ? headerStyleFail : headerStyleSuccess;

		if (!mobileView) return null;
		if (createNodeFail || createNodeSuccess) {
			return (
				<h5
					className={createNodeFail ? 'notification-open-create-node-fail' : 'notification-open-create-node-success'}
					style={headerStyle}
				>
					{createNodeFail
						? 'The node could not be created. Maybe a node with the specified name already exists.'
						: 'Node created successfully.'}
				</h5>
			);
		}
		return <div style={emptyHeaderStyle}></div>;
	};

	const handlePickSystem = (e, newValue: string): void => {
		if (newValue) {
			setPickedSystem(newValue);
		}
	};

	const handlePickDevice = (e, newValue: string): void => {
		if (newValue) {
			setDeviceName(newValue);
		}
	};

	if (systems.length < 1) {
		return (
			<Card>
				<CardHeader color='info' icon>
					<CardIcon color='info'>
						<Node />
					</CardIcon>
					<h4 className={classes.cardIconTitle}>Create Node</h4>
				</CardHeader>
				<CardBody>Please add a system first.</CardBody>
			</Card>
		);
	}

	try {
		const systemOptions = systems.map((system) => system.name);
		const deviceOptions = filteredDevices.map((device) => device.SerialString).sort();

		return (
			<ErrorComponent message='create node form crashed' Children>
				<Card className='createNodeForm'>
					<CardHeader color='info' icon>
						<CardIcon color='info'>
							<Node />
						</CardIcon>
						<h4 className={classes.cardIconTitle} data-testid='createNodeHeader'>
							Create Node for System
						</h4>
					</CardHeader>
					<CardBody>
						<div style={{ width: '100%' }}>
							<Autocomplete
								options={systemOptions}
								getOptionLabel={(system) => system}
								id='system-name'
								value={pickedSystemName}
								onChange={(event, newValue) => handlePickSystem(event, newValue)}
								renderInput={(systems) => <TextField {...systems} label='System' margin='normal' />}
								style={{ width: mobileView ? '100%' : '50%', transition: '0.3s' }}
							/>
						</div>
						<div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
							<Autocomplete
								options={deviceOptions}
								getOptionLabel={(device) => device}
								id='available-devices'
								value={deviceName}
								onChange={(event, newValue) => handlePickDevice(event, newValue)}
								renderInput={(filteredDevices) => <TextField {...filteredDevices} label='Device serial number' margin='normal' />}
								style={{ width: mobileView ? '100%' : '50%', transition: '0.3s' }}
							/>
							<div style={{ transform: 'translateY(10px)' }}>{onRefreshButtonClick()}</div>
						</div>
						<FormControl style={{ width: '100%' }}>
							<TextField
								label='Node name'
								id='node_name'
								data-testid='inputNodeName'
								inputProps={{
									value: inputValue,
									maxLength: utils.defaultMaxInputLength,
									autoCapitalize: 'off',
								}}
								onChange={(e) => handleInputChange(e)}
								fullWidth
								style={{ marginTop: '16px', minWidth: '100%' }}
								error={error}
								helperText={error ? 'Incorrect name. Characters not allowed: <, >, /, \\, &, ; ' : ''}
							></TextField>
						</FormControl>
						{renderMobileNotifications()}
						{onCreateNodeButtonClick()}
					</CardBody>
					{!mobileView ? (
						<Snackbar
							close
							closeNotification={() => closeCreateNodeNotification()}
							color={createNodeFail ? 'danger' : 'success'}
							message={
								createNodeFail
									? `The node could not be created. Maybe a node with the specified name already exists.`
									: 'Node created successfully.'
							}
							open={createNodeFail || createNodeSuccess}
							place='tc'
						/>
					) : null}
				</Card>
			</ErrorComponent>
		);
	} catch (error) {
		return <ErrorText message='Create Node Form crashed'></ErrorText>;
	}
};

const mapStateToProps = (state) => ({
	FreeDevices: state.dashboard.FreeDevices,
	disableRefreshButton: state.dashboard.disableRefreshButton,
	disableCreateNodeButton: state.dashboard.disableCreateNodeButton,
	createNodeFail: state.dashboard.createNodeFail,
	createNodeSuccess: state.dashboard.createNodeSuccess,
	mobileView: state.dashboard.mobileView,
});
const actions = { addNode, getFreeDevices };

const CreateNodeFormWithStyles = regularFormsStyle(CreateNodeForm);
export default connect(mapStateToProps, actions)(CreateNodeFormWithStyles);
